/**
 * Media Gifts Service
 *
 * Domain layer for the "gifts received" list on a piece of content — the
 * content owner's view of who has gifted / unlocked it. Datasource-agnostic:
 * all Firestore access is delegated to firestoreService, so this service can
 * be swapped onto a different backend without touching the domain-level
 * orchestration here.
 *
 * Mirrors Flutter's GiftsRepository.getMediaGifts pass-through
 * (lib/data/repository/gifts_repository.dart:35-45), which delegates to
 * ContentReactionsDataSource.getMediaGifts and returns the
 * { transactions, hasMore, lastFetchedItem } page shape
 * (lib/data/responses/transactions_pagination_response.dart:1-10).
 *
 * NOTE: this is the gifts-received READ path. Send-gift logic lives in
 * giftService.ts — a distinct responsibility, intentionally not merged here.
 */

import type { PageCursor } from '@/data/types/json';
import { firestoreService } from '@/data/services/firestoreService';
import { AppTransactionRemapper } from '@/domain/remappers/transaction/appTransactionRemapper';
import type { AppTransactionEntity } from '@/domain/entities/transaction/appTransactionEntity';

export interface MediaGiftsPage {
  gifts: AppTransactionEntity[];
  hasMore: boolean;
  cursor?: PageCursor;
}

export class MediaGiftsService {
  /**
   * Paginated fetch of gift/unlock transactions for a piece of content.
   * Remaps each raw Firestore document through the AppTransaction remapper
   * (fromJson → toEntity); the data layer is never cast directly to a DTO.
   */
  async fetchMediaGifts(
    mediaPath: string,
    limitCount: number,
    cursor?: PageCursor,
  ): Promise<MediaGiftsPage> {
    const page = await firestoreService.getMediaGifts(mediaPath, limitCount, cursor);
    return {
      gifts: page.items.map(item => AppTransactionRemapper.toEntity(AppTransactionRemapper.fromJson(item))),
      hasMore: page.hasMore,
      cursor: page.cursor,
    };
  }
}

export const mediaGiftsService = new MediaGiftsService();
